import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, PieChart, Pie, Cell } from "recharts";
import { Loader2, KeyRound, Copy, CheckCircle2, PauseCircle, XCircle, Trash2, Plus, ShieldCheck, RefreshCw, Sparkles, FileDown } from "lucide-react";
import { api, apiError } from "../../lib/api";
import { AiCompare } from "./AiCompare";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const PERIOD_LABELS = { week: "Semaine", month: "Mois", quarter: "Trimestre", year: "Année" };

function LicMarkdown({ text }) {
  const lines = (text || "").split("\n"); const out = []; let b = [];
  const flush = (i) => { if (b.length) { out.push(<ul key={`u${i}`} className="my-2 ml-4 list-disc space-y-1">{b}</ul>); b = []; } };
  const inl = (s) => s.split(/(\*\*[^*]+\*\*)/g).map((p, j) => p.startsWith("**") && p.endsWith("**") ? <strong key={j} className="text-navy">{p.slice(2, -2)}</strong> : <span key={j}>{p}</span>);
  lines.forEach((ln, i) => { const t = ln.trim(); if (!t || t === "---" || /^\|[-\s|]+\|$/.test(t)) { flush(i); return; }
    if (t.startsWith("### ")) { flush(i); out.push(<h5 key={i} className="mt-2 mb-1 font-semibold text-navy">{inl(t.slice(4))}</h5>); }
    else if (t.startsWith("## ")) { flush(i); out.push(<h4 key={i} className="mt-3 mb-1 font-display text-sm font-semibold text-navy">{inl(t.slice(3))}</h4>); }
    else if (t.startsWith("# ")) { flush(i); out.push(<h4 key={i} className="mt-2 mb-1 font-display text-base font-bold text-navy">{inl(t.slice(2))}</h4>); }
    else if (t.startsWith("- ") || t.startsWith("* ")) b.push(<li key={i} className="text-sm text-slate-600">{inl(t.slice(2))}</li>);
    else if (t.startsWith("|")) { flush(i); const cells = t.split("|").map((c) => c.trim()).filter(Boolean); out.push(<p key={i} className="my-0.5 text-sm text-slate-600">{cells.map((c, k) => <span key={k}>{k ? " · " : ""}{inl(c)}</span>)}</p>); }
    else { flush(i); out.push(<p key={i} className="my-1 text-sm text-slate-600">{inl(t)}</p>); } });
  flush("end"); return <div>{out}</div>;
}

const arrow = (d) => d?.dir === "up" ? "▲" : d?.dir === "down" ? "▼" : "▬";
const arrowCls = (d) => d?.dir === "up" ? "text-emerald-600" : d?.dir === "down" ? "text-red-600" : "text-slate-400";
const pctTxt = (d) => d?.pct == null ? "n/a" : `${d.pct > 0 ? "+" : ""}${d.pct} %`;

const STATUS = {
  active: { label: "Active", cls: "bg-emerald-100 text-emerald-700" },
  pending: { label: "En attente", cls: "bg-amber-100 text-amber-700" },
  suspended: { label: "Suspendue", cls: "bg-orange-100 text-orange-700" },
  revoked: { label: "Révoquée", cls: "bg-red-100 text-red-700" },
  expired: { label: "Expirée", cls: "bg-slate-200 text-slate-600" },
};

export function LicensesManager() {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [durations, setDurations] = useState({});
  const [secteurs, setSecteurs] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [aiPeriod, setAiPeriod] = useState("month");
  const [aiBusy, setAiBusy] = useState(false);
  const [ai, setAi] = useState(null);
  const panelRef = useRef(null);
  const [nf, setNf] = useState({ establishment: "", responsable: "", email: "", telephone: "", postes: 1, secteur: "", duration: "12m" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/licenses${filter ? `?status=${filter}` : ""}`);
      setRows(data.licenses); setCounts(data.counts); setDurations(data.durations); setSecteurs(data.secteurs || []);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const patch = async (id, body, msg) => { try { await api.patch(`/admin/licenses/${id}`, body); toast.success(msg); load(); } catch (e) { toast.error(apiError(e)); } };
  const validateDemo = async (id, duration = "essai") => { try { await api.post(`/admin/licenses/${id}/validate-demo`, { duration }); toast.success("Démo validée — clé générée"); load(); } catch (e) { toast.error(apiError(e)); } };
  const del = async (id) => { if (!window.confirm("Supprimer cette licence ?")) return; try { await api.delete(`/admin/licenses/${id}`); toast.success("Supprimée"); load(); } catch (e) { toast.error(apiError(e)); } };
  const create = async () => {
    if (!nf.establishment.trim()) { toast.error("Nom d'établissement requis"); return; }
    setCreating(true);
    try { await api.post("/admin/licenses", { ...nf, postes: Number(nf.postes) || 1 }); toast.success("Licence créée"); setNf({ establishment: "", responsable: "", email: "", telephone: "", postes: 1, secteur: "", duration: "12m" }); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setCreating(false); }
  };
  const copyKey = (k) => { navigator.clipboard?.writeText(k); toast.success("Clé copiée"); };

  const runAi = async () => {
    setAiBusy(true); setAi(null);
    try { const { data } = await api.post(`/admin/licenses/analyse-ia?period=${aiPeriod}`, {}, { timeout: 90000 }); setAi(data); if (!data.ai_available) toast.info("Bilan chiffré affiché (clé IA indisponible)"); }
    catch (e) { toast.error(apiError(e, "Analyse impossible")); } finally { setAiBusy(false); }
  };
  const exportAiPdf = async () => {
    if (!panelRef.current) return;
    try {
      const [{ default: h2c }, { jsPDF }] = await Promise.all([import("html2canvas"), import("jspdf")]);
      const canvas = await h2c(panelRef.current, { scale: 2, backgroundColor: "#fff" });
      const img = canvas.toDataURL("image/jpeg", 0.92);
      const pdf = new jsPDF("p", "mm", "a4"); const pw = pdf.internal.pageSize.getWidth(); const ph = pdf.internal.pageSize.getHeight();
      const h = (canvas.height * pw) / canvas.width; let left = h; let pos = 0;
      pdf.addImage(img, "JPEG", 0, pos, pw, h); left -= ph;
      while (left > 0) { pos -= ph; pdf.addPage(); pdf.addImage(img, "JPEG", 0, pos, pw, h); left -= ph; }
      pdf.save(`bilan-licences-${aiPeriod}.pdf`); toast.success("PDF généré");
    } catch { toast.error("Export PDF impossible"); }
  };

  return (
    <div data-testid="licenses-manager">
      <div className="mb-4 flex items-center gap-2 rounded-xl bg-[#0E7C7B]/10 px-4 py-3 text-[#0b6463]">
        <ShieldCheck size={18} />
        <span className="text-sm font-semibold">Gestion des licences — Patient Vitalis Care (com.carissante.patientvitalis)</span>
      </div>

      {/* Analyse IA */}
      <div className="mb-4 rounded-2xl border border-violet-200 bg-violet-50/40 p-4" data-testid="lic-ai-panel">
        <div className="flex flex-wrap items-center gap-2">
          <Sparkles size={16} className="text-violet-600" />
          <span className="text-sm font-semibold text-navy">Analyse IA — bilan des licences</span>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <Select value={aiPeriod} onValueChange={setAiPeriod}>
              <SelectTrigger className="h-9 w-36 text-sm" data-testid="lic-ai-period"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(PERIOD_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
            <Button size="sm" onClick={runAi} disabled={aiBusy} data-testid="lic-ai-run" className="rounded-full bg-violet-600 px-3 text-xs text-white hover:bg-violet-700">
              {aiBusy ? <><Loader2 size={13} className="mr-1 animate-spin" /> Analyse…</> : <><Sparkles size={13} className="mr-1" /> Analyse IA</>}
            </Button>
            <AiCompare moduleKey="licenses" params={{ period: aiPeriod }} />
            {ai && <Button size="sm" variant="outline" onClick={exportAiPdf} data-testid="lic-ai-pdf" className="rounded-full text-xs"><FileDown size={13} className="mr-1" /> PDF</Button>}
          </div>
        </div>
        {aiBusy && <p className="mt-2 text-xs text-slate-500">Génération du bilan (postes, statuts, revenus, tendances, recommandations)…</p>}
        {ai && (
          <div ref={panelRef} className="mt-3 rounded-xl bg-white p-4" data-testid="lic-ai-result">
            <h3 className="font-display text-base font-bold text-navy">Bilan des licences — {ai.periode}</h3>
            <div className="mt-3 grid gap-2 sm:grid-cols-4">
              {[["Licences", ai.courant.total_licences, ai.evolution.licences], ["Postes", ai.courant.total_postes, ai.evolution.postes],
                ["Revenus (FCFA)", new Intl.NumberFormat("fr-FR").format(ai.courant.revenus_total), ai.evolution.revenus], ["Actives", ai.courant.statuts.active, ai.evolution.actives]].map(([l, v, d]) => (
                <div key={l} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <p className="text-[11px] text-slate-500">{l}</p>
                  <p className="font-display text-xl font-bold text-navy">{v}</p>
                  <p className={`text-[11px] font-medium ${arrowCls(d)}`}>{arrow(d)} {pctTxt(d)} vs préc.</p>
                </div>
              ))}
            </div>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <div className="rounded-lg border border-slate-100 p-3">
                <p className="mb-1 text-xs font-semibold text-navy">Statuts (période)</p>
                {Object.entries(ai.courant.statuts).map(([k, v]) => <span key={k} className="mr-2 text-[11px] text-slate-600">{STATUS[k]?.label || k}: <b>{v}</b></span>)}
                <p className="mt-2 text-xs text-slate-600">Taux de renouvellement : <b>{ai.taux_renouvellement}%</b></p>
              </div>
              <div className="rounded-lg border border-slate-100 p-3">
                <p className="mb-1 text-xs font-semibold text-navy">Revenus par durée (FCFA)</p>
                {Object.entries(ai.courant.revenus_par_duree).filter(([, v]) => v > 0).map(([k, v]) => <div key={k} className="text-[11px] text-slate-600">{ai.duration_labels[k]}: <b>{new Intl.NumberFormat("fr-FR").format(v)}</b></div>)}
                {ai.courant.revenus_total === 0 && <p className="text-[11px] italic text-slate-400">Aucun revenu lié sur la période.</p>}
              </div>
            </div>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <div className="rounded-lg border border-amber-100 bg-amber-50/50 p-3">
                <p className="mb-1 text-xs font-semibold text-amber-700">Proches de l'expiration ({ai.proche_expiration.length})</p>
                {ai.proche_expiration.slice(0, 6).map((p, i) => <div key={i} className="text-[11px] text-slate-600">{p.establishment} — {p.expires_at ? new Date(p.expires_at).toLocaleDateString("fr-FR") : "—"}</div>)}
                {!ai.proche_expiration.length && <p className="text-[11px] italic text-slate-400">Aucune.</p>}
              </div>
              <div className="rounded-lg border border-slate-100 p-3">
                <p className="mb-1 text-xs font-semibold text-navy">Établissements les plus actifs</p>
                {ai.etablissements_actifs.slice(0, 6).map((e, i) => <div key={i} className="text-[11px] text-slate-600">{e.establishment} — {e.licences} licence(s)</div>)}
              </div>
            </div>
            {ai.courant.par_secteur && Object.keys(ai.courant.par_secteur).length > 0 && (
              <div className="mt-3 grid gap-3 lg:grid-cols-2" data-testid="lic-ai-secteur-chart">
                <div className="rounded-lg border border-slate-100 p-3">
                  <p className="mb-2 text-xs font-semibold text-navy">Licences & postes par secteur</p>
                  <div className="h-56 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={Object.keys(ai.courant.par_secteur).map((s) => ({ secteur: s, licences: ai.courant.par_secteur[s], postes: (ai.courant.postes_par_secteur || {})[s] || 0 }))} margin={{ top: 5, right: 8, left: -18, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="secteur" tick={{ fontSize: 10 }} interval={0} angle={-15} textAnchor="end" height={50} />
                        <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                        <Tooltip />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <Bar dataKey="licences" name="Licences" fill="#0E7C7B" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="postes" name="Postes" fill="#7c3aed" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                <div className="rounded-lg border border-slate-100 p-3" data-testid="lic-ai-secteur-pie">
                  <p className="mb-2 text-xs font-semibold text-navy">Répartition des licences par secteur</p>
                  <div className="h-56 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={Object.keys(ai.courant.par_secteur).map((s) => ({ name: s, value: ai.courant.par_secteur[s] }))}
                          dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} label={(e) => `${e.name} (${e.value})`} labelLine={false} fontSize={9}>
                          {Object.keys(ai.courant.par_secteur).map((s, i) => <Cell key={s} fill={["#0E7C7B", "#7c3aed", "#0ea5e9", "#f59e0b", "#ef4444", "#10b981", "#6366f1", "#ec4899", "#64748b"][i % 9]} />)}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            )}
            {ai.narrative && <div className="mt-3 border-t border-slate-100 pt-2"><LicMarkdown text={ai.narrative} /></div>}
          </div>
        )}
      </div>

      {/* Compteurs + filtre */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        {Object.entries(STATUS).map(([k, s]) => (
          <span key={k} className={`rounded-full px-3 py-1 text-xs font-semibold ${s.cls}`}>{s.label} : {counts[k] || 0}</span>
        ))}
        <div className="ml-auto w-48">
          <Select value={filter || "all"} onValueChange={(v) => setFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="h-9 text-sm" data-testid="lic-filter"><SelectValue placeholder="Filtrer" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les statuts</SelectItem>
              {Object.entries(STATUS).map(([k, s]) => <SelectItem key={k} value={k}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Création manuelle */}
      <div className="mb-4 rounded-xl border border-slate-200 bg-white p-4">
        <p className="mb-2 text-xs font-semibold text-navy">Créer une licence manuellement</p>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-8">
          <Input placeholder="Établissement" value={nf.establishment} onChange={(e) => setNf({ ...nf, establishment: e.target.value })} data-testid="lic-new-establishment" className="h-9 text-sm lg:col-span-2" />
          <Input placeholder="Responsable" value={nf.responsable} onChange={(e) => setNf({ ...nf, responsable: e.target.value })} data-testid="lic-new-responsable" className="h-9 text-sm" />
          <Input placeholder="Email" value={nf.email} onChange={(e) => setNf({ ...nf, email: e.target.value })} data-testid="lic-new-email" className="h-9 text-sm" />
          <Input type="number" min="1" placeholder="Postes" value={nf.postes} onChange={(e) => setNf({ ...nf, postes: e.target.value })} data-testid="lic-new-postes" className="h-9 text-sm" />
          <Select value={nf.secteur || "none"} onValueChange={(v) => setNf({ ...nf, secteur: v === "none" ? "" : v })}>
            <SelectTrigger className="h-9 text-sm" data-testid="lic-new-secteur"><SelectValue placeholder="Secteur" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Secteur…</SelectItem>
              {secteurs.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={nf.duration} onValueChange={(v) => setNf({ ...nf, duration: v })}>
            <SelectTrigger className="h-9 text-sm" data-testid="lic-new-duration"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(durations).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
          </Select>
          <Button onClick={create} disabled={creating} data-testid="lic-new-create" className="h-9 rounded-full bg-[#0E7C7B] text-white hover:bg-[#0b6463]">{creating ? <Loader2 size={14} className="animate-spin" /> : <><Plus size={14} className="mr-1" /> Créer</>}</Button>
        </div>
      </div>

      {/* En-tête tableau + bouton Analyse IA directement au-dessus */}
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-display text-sm font-semibold text-navy">Licences des établissements</h3>
        <Button size="sm" onClick={runAi} disabled={aiBusy} data-testid="lic-table-ai"
          className="rounded-full bg-violet-600 px-3 text-xs text-white hover:bg-violet-700">
          {aiBusy ? <><Loader2 size={13} className="mr-1 animate-spin" /> Analyse…</> : <><Sparkles size={13} className="mr-1" /> Analyse IA</>}
        </Button>
      </div>

      {/* Tableau */}
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs text-slate-500">
            <tr><th className="px-3 py-2">Établissement</th><th className="px-3 py-2">Secteur</th><th className="px-3 py-2">Clé</th><th className="px-3 py-2">Postes</th><th className="px-3 py-2">Durée</th><th className="px-3 py-2">Expiration</th><th className="px-3 py-2">Statut</th><th className="px-3 py-2 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="py-8 text-center"><Loader2 className="mx-auto animate-spin text-[#0E7C7B]" /></td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={8} className="py-6 text-center text-xs text-slate-400">Aucune licence.</td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100" data-testid={`lic-row-${r.id}`}>
                <td className="px-3 py-2"><div className="font-medium text-navy">{r.establishment}</div><div className="text-[11px] text-slate-400">{r.email || "—"} · {r.source}{r.offre && r.offre !== "Patient Vitalis Care" ? ` · ${r.offre}` : ""}</div></td>
                <td className="px-3 py-2">
                  <Select value={r.secteur || "none"} onValueChange={(v) => patch(r.id, { secteur: v === "none" ? "" : v }, "Secteur mis à jour")}>
                    <SelectTrigger className="h-8 w-36 text-xs" data-testid={`lic-secteur-${r.id}`}><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Non renseigné</SelectItem>
                      {secteurs.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-3 py-2">
                  {r.license_key ? (
                    <button onClick={() => copyKey(r.license_key)} className="flex items-center gap-1 font-mono text-xs text-[#0E7C7B] hover:underline" data-testid={`lic-key-${r.id}`}><KeyRound size={12} /> {r.license_key} <Copy size={11} /></button>
                  ) : <span className="text-[11px] italic text-slate-400">non générée</span>}
                </td>
                <td className="px-3 py-2">{r.postes}</td>
                <td className="px-3 py-2 text-xs">{durations[r.duration] || r.duration}</td>
                <td className="px-3 py-2 text-xs text-slate-500">{r.expires_at ? new Date(r.expires_at).toLocaleDateString("fr-FR") : (r.status === "active" ? "À vie" : "—")}</td>
                <td className="px-3 py-2"><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS[r.status]?.cls || "bg-slate-100"}`}>{STATUS[r.status]?.label || r.status}</span></td>
                <td className="px-3 py-2">
                  <div className="flex items-center justify-end gap-1">
                    {r.status === "pending" && <button onClick={() => validateDemo(r.id, "essai")} title="Valider la démo" data-testid={`lic-validate-${r.id}`} className="rounded p-1.5 text-emerald-600 hover:bg-emerald-50"><CheckCircle2 size={16} /></button>}
                    {r.status !== "active" && r.status !== "pending" && <button onClick={() => patch(r.id, { status: "active" }, "Licence activée")} title="Activer" data-testid={`lic-activate-${r.id}`} className="rounded p-1.5 text-emerald-600 hover:bg-emerald-50"><CheckCircle2 size={16} /></button>}
                    {r.status === "active" && <button onClick={() => patch(r.id, { status: "suspended" }, "Licence suspendue")} title="Suspendre" data-testid={`lic-suspend-${r.id}`} className="rounded p-1.5 text-orange-600 hover:bg-orange-50"><PauseCircle size={16} /></button>}
                    <button onClick={() => patch(r.id, { status: "revoked" }, "Licence révoquée")} title="Révoquer" data-testid={`lic-revoke-${r.id}`} className="rounded p-1.5 text-red-600 hover:bg-red-50"><XCircle size={16} /></button>
                    <button onClick={() => del(r.id)} title="Supprimer" data-testid={`lic-del-${r.id}`} className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-red-500"><Trash2 size={15} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button onClick={load} className="mt-3 flex items-center gap-1 text-xs text-slate-500 hover:text-ciel"><RefreshCw size={12} /> Actualiser</button>
    </div>
  );
}
